import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { format } from "date-fns";
import ApiService from "../../services/ApiService";

const ReceivedFaxes = () => {
  const [faxes, setFaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advancedSearch, setAdvancedSearch] = useState({
    toNumber: "",
    date: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFaxes, setTotalFaxes] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFax] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const itemsPerPage = 10;

  const fetchFaxes = useCallback(
    async (searchData = null) => {
      try {
        setLoading(true);
        let response;

        if (searchData && (searchData.toNumber || searchData.date)) {
          // Use advanced search endpoint when search parameters are provided
          response = await ApiService.searchReceivedFaxes(
            searchData.toNumber,
            searchData.date
          );

          // For search results, we don't have pagination info
          const faxesData =
            response.incomingFaxes ||
            response.data ||
            response.faxes ||
            response;
          setFaxes(Array.isArray(faxesData) ? faxesData : []);
          setTotalFaxes(Array.isArray(faxesData) ? faxesData.length : 0);
          setTotalPages(1);
        } else {
          // Use regular paginated endpoint for initial load
          response = await ApiService.getReceivedFaxes(
            currentPage,
            itemsPerPage,
            ""
          );

          // Handle the new response structure with incomingFaxes array
          const faxesData =
            response.incomingFaxes ||
            response.data ||
            response.faxes ||
            response;
          const totalCount = response.totalCount || response.total || 0;

          setFaxes(Array.isArray(faxesData) ? faxesData : []);
          setTotalFaxes(totalCount);
          setTotalPages(Math.ceil(totalCount / itemsPerPage));
        }
      } catch (error) {
        console.error("Error fetching faxes:", error);
        setFaxes([]);
        setTotalFaxes(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, itemsPerPage]
  );

  useEffect(() => {
    fetchFaxes();
  }, [currentPage, fetchFaxes]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchFaxes(advancedSearch);
  };

  const handleAdvancedSearchChange = (field, value) => {
    setAdvancedSearch((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearSearch = () => {
    setAdvancedSearch({
      toNumber: "",
      date: "",
    });
    setCurrentPage(1);
    fetchFaxes();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchFaxes();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      received: "success",
      processing: "warning",
      failed: "danger",
      pending: "info",
    };
    return <Badge color={statusColors[status] || "secondary"}>{status}</Badge>;
  };

  const viewFax = async (fax) => {
    // Check for fileUrl or filePath as backup
    const pdfUrl = fax.fileUrl || fax.filePath || null;

    if (pdfUrl) {
      // If filePath starts with /, assume it needs base URL
      const fullUrl = pdfUrl.startsWith("/")
        ? `https://99text.com${pdfUrl}`
        : pdfUrl;
      window.open(fullUrl, "_blank");
    } else {
      console.error("No fileUrl or filePath available for this fax");
    }
  };

  const downloadFax = async (fax) => {
    try {
      // Check for fileUrl or filePath as backup
      const pdfUrl = fax.fileUrl || fax.filePath || null;

      if (pdfUrl) {
        // If filePath starts with /, assume it needs base URL
        const fullUrl = pdfUrl.startsWith("/")
          ? `https://99text.com${pdfUrl}`
          : pdfUrl;
        await ApiService.downloadFaxPdf(
          fullUrl,
          fax.fileName || `fax_${fax.id}.pdf`
        );
      } else {
        console.error("No fileUrl or filePath available for this fax");
      }
    } catch (error) {
      console.error("Error downloading fax:", error);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  return (
    <div className="app-main__inner">
      <div className="app-page-title">
        <div className="page-title-wrapper">
          <div className="page-title-heading">
            <div className="page-title-icon">
              <i className="pe-7s-mail-open-file icon-gradient bg-mean-fruit"></i>
            </div>
            <div>
              Received Faxes
              <div className="page-title-subheading">
                View and manage received fax documents
              </div>
            </div>
          </div>
        </div>
      </div>

      <Row>
        <Col md="12">
          <Card className="main-card mb-3">
            <CardHeader>
              <i className="header-icon pe-7s-drawer icon-gradient bg-plum-plate"></i>
              Fax Inbox
            </CardHeader>
            <CardBody>
              {/* Search Section */}
              <Card className="mb-3">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <h6 className="mb-0 me-3">Search Faxes</h6>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handleClearSearch}
                    >
                      Clear
                    </Button>
                  </div>

                  <form onSubmit={handleSearch}>
                    <Row>
                      <Col md="4">
                        <Input
                          type="text"
                          placeholder="DID Number (e.g., +1555123456)"
                          value={advancedSearch.toNumber}
                          onChange={(e) =>
                            handleAdvancedSearchChange(
                              "toNumber",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                      <Col md="4">
                        <Input
                          type="date"
                          value={advancedSearch.date}
                          onChange={(e) =>
                            handleAdvancedSearchChange("date", e.target.value)
                          }
                        />
                      </Col>
                      <Col md="4">
                        <Button
                          color="primary"
                          type="submit"
                          className="me-2"
                          style={{ color: "white" }}
                        >
                          <i className="fa fa-search"></i> Search
                        </Button>
                      </Col>
                    </Row>
                  </form>
                </CardBody>
              </Card>
              {loading ? (
                <div className="text-center">
                  <div className="loader-container-inner">
                    <div className="text-center">Loading faxes...</div>
                  </div>
                </div>
              ) : (
                <>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Customer</th>
                        <th>DID Number</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faxes.length > 0 ? (
                        faxes.map((fax) => (
                          <tr key={fax.id}>
                            <td>{formatDate(fax.receivedAt)}</td>
                            <td>{fax.fromNumber}</td>
                            <td>{fax.didNumber}</td>
                            <td>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => viewFax(fax)}
                                className="me-2"
                                style={{ color: "white" }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No faxes found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {totalFaxes > 0 && (
                    <Row className="mt-3">
                      <Col className="d-flex justify-content-between align-items-center">
                        <div className="text-muted">
                          Showing {(currentPage - 1) * itemsPerPage + 1}-
                          {Math.min(currentPage * itemsPerPage, totalFaxes)} of{" "}
                          {totalFaxes} total faxes
                        </div>
                        <div className="d-flex align-items-center">
                          {totalPages > 1 && (
                            <>
                              <span className="me-3 text-muted">
                                Page {currentPage} of {totalPages}
                              </span>
                              <div className="btn-group">
                                <Button
                                  color="primary"
                                  outline
                                  size="sm"
                                  disabled={currentPage <= 1}
                                  onClick={() => setCurrentPage(1)}
                                  title="First Page"
                                >
                                  <i className="fa fa-angle-double-left"></i>
                                </Button>
                                <Button
                                  color="primary"
                                  outline
                                  size="sm"
                                  disabled={currentPage <= 1}
                                  onClick={() =>
                                    setCurrentPage(currentPage - 1)
                                  }
                                  title="Previous Page"
                                >
                                  <i className="fa fa-chevron-left"></i>
                                </Button>

                                {/* Page Numbers */}
                                {Array.from(
                                  { length: Math.min(5, totalPages) },
                                  (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                      pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                      pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                      pageNum = totalPages - 4 + i;
                                    } else {
                                      pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                      <Button
                                        key={pageNum}
                                        color={
                                          currentPage === pageNum
                                            ? "primary"
                                            : "primary"
                                        }
                                        outline={currentPage !== pageNum}
                                        size="sm"
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={
                                          currentPage === pageNum
                                            ? "active"
                                            : ""
                                        }
                                      >
                                        {pageNum}
                                      </Button>
                                    );
                                  }
                                )}

                                <Button
                                  color="primary"
                                  outline
                                  size="sm"
                                  disabled={currentPage >= totalPages}
                                  onClick={() =>
                                    setCurrentPage(currentPage + 1)
                                  }
                                  title="Next Page"
                                >
                                  <i className="fa fa-chevron-right"></i>
                                </Button>
                                <Button
                                  color="primary"
                                  outline
                                  size="sm"
                                  disabled={currentPage >= totalPages}
                                  onClick={() => setCurrentPage(totalPages)}
                                  title="Last Page"
                                >
                                  <i className="fa fa-angle-double-right"></i>
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* View Fax Modal */}
      <Modal
        isOpen={viewModalOpen}
        toggle={() => setViewModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setViewModalOpen(false)}>
          Fax Details
        </ModalHeader>
        <ModalBody>
          {selectedFax && (
            <div>
              <Row>
                <Col md="6">
                  <p>
                    <strong>From:</strong> {selectedFax.senderNumber}
                  </p>
                  <p>
                    <strong>Sender Name:</strong>{" "}
                    {selectedFax.senderName || "Unknown"}
                  </p>
                  <p>
                    <strong>Received:</strong>{" "}
                    {formatDate(selectedFax.receivedAt)}
                  </p>
                </Col>
                <Col md="6">
                  <p>
                    <strong>Pages:</strong> {selectedFax.pageCount}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedFax.status)}
                  </p>
                  <p>
                    <strong>Subject:</strong>{" "}
                    {selectedFax.subject || "No Subject"}
                  </p>
                </Col>
              </Row>
              {selectedFax.message && (
                <div>
                  <h6>Message:</h6>
                  <p className="bg-light p-3 rounded">{selectedFax.message}</p>
                </div>
              )}
              {/* TODO: Add PDF preview here if needed */}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={() =>
              selectedFax && downloadFax(selectedFax.id, selectedFax.filename)
            }
          >
            Download
          </Button>
          <Button color="secondary" onClick={() => setViewModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReceivedFaxes;
